import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpsertAdministratorRequestBody } from '@/src/apis/rest/administrators.dtos';
import { postAdministratorsAPI } from '@/src/apis/rest/administrators';
import RadioGroup from '@/components/atom/RadioGroup';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
} from '@/src/pages/Administrator/upsertField';
import { OPTIONS_CONST } from '@/src/constants/options';
import Typo from '@/components/atom/Typo';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { useRef } from 'react';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { formatter } from '@/src/utils/formatter';

const BACK_PATH = PATH.ADMINISTRATOR_LIST;

function AdministratorCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<UpsertAdministratorRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // mutation
  const postAdministrator = useMutation({ mutationFn: postAdministratorsAPI });

  const isFormValid = form.formState.isValid;

  // state
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // event
  const handleSubmit = (values: UpsertAdministratorRequestBody) => {
    postAdministrator.mutate(values, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        navigate(BACK_PATH);
      },
      onError: e => {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: e.message,
        });
      },
    });
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(BACK_PATH)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              이전 화면
            </div>
          </Button>
          <Button
            type="button"
            onClick={openSaveDialog}
            className="w-40"
            buttonSize="medium"
            disabled={!isFormValid}
            isLoading={postAdministrator.isPending}
          >
            <div className="flex items-center gap-x-2">저장</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={['관리자 관리', '관리자 등록']}
        pageTitle="관리자 등록"
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="관리자 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.aId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.aId.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.aId.placeholder}
                          onClear={() => field.onChange('')}
                          {...field}
                          onBlur={() => {
                            form?.trigger('aId');
                            field?.onBlur();
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.name.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.name.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.name.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={20}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.department.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.department.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.department.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={20}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.phone.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.phone.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.phone.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={13}
                          {...field}
                          onBlur={() => {
                            form?.trigger('phone');
                            field?.onBlur();
                          }}
                          onChange={e => {
                            field.onChange(
                              formatter.NUM_HYPHEN_INSTANTLY(e.target.value),
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isActivated.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.isActivated.label}
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.activated, value: 'true' },
                            {
                              label: OPTIONS_CONST.notActivated,
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </div>
            <div className="mt-7">
              <Typo
                typoType="b16r"
                typoColor="gray9"
              >{`* 초기 비밀번호는 'ecoya1q2w3e!@' 입니다.`}</Typo>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postAdministrator.isPending}
        />
      )}
    </>
  );
}

export default AdministratorCreatePage;
